'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Clock, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { login, signup } from '@/lib/api/auth';
import { apiRequest } from '@/lib/api/client';

type AuthTab = 'login' | 'signup' | 'forgot';

export default function AuthContent() {
  const router = useRouter();
  const [tab, setTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'freelancer' | 'client'>('freelancer');
  const [forgotSent, setForgotSent] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginRemember, setLoginRemember] = useState(false);
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});

  // Signup form state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupErrors, setSignupErrors] = useState<Record<string, string>>({});

  // Forgot form state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotErrors, setForgotErrors] = useState<Record<string, string>>({});

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!loginEmail) newErrors.email = 'Email is required';
    if (!loginPassword) newErrors.password = 'Password is required';
    setLoginErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const data = await login(loginEmail, loginPassword);
      localStorage.setItem('tt-role', data.user.activeRole);
      toast.success('Welcome back!');
      router.push('/dashboard');
    } catch (err: any) {
      toast.error(err?.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!signupName) newErrors.name = 'Name is required';
    if (!signupEmail) newErrors.email = 'Email is required';
    if (!signupPassword) newErrors.password = 'Password is required';
    else if (signupPassword.length < 8) newErrors.password = 'Minimum 8 characters';
    if (!signupConfirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (signupPassword !== signupConfirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setSignupErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      await signup(signupName, signupEmail, signupPassword, selectedRole);
      toast.success('Account created! You can now sign in.');
      setTab('login');
      setLoginEmail(signupEmail);
    } catch (err: any) {
      toast.error(err?.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!forgotEmail) newErrors.email = 'Email is required';
    setForgotErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      await apiRequest('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: forgotEmail }) });
    } catch {
      // Always show success to prevent email enumeration
    } finally {
      setForgotSent(true);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      {/* Left panel — brand */}
      <div
        className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] flex-shrink-0 p-10 relative overflow-hidden"
        style={{ backgroundColor: '#000000' }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-10"
            style={{ backgroundColor: 'var(--primary)', filter: 'blur(80px)' }} />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-8"
            style={{ backgroundColor: 'var(--primary)', filter: 'blur(80px)' }} />
        </div>

        <div className="flex items-center gap-3 relative">
          <AppLogo height={40} variant="light" />
        </div>

        <div className="relative space-y-6">
          <div>
            <h1 className="text-display text-white leading-tight mb-3">
              Track time.<br />Get paid faster.
            </h1>
            <p className="text-body-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>
              The free time tracking platform built for freelancers and the clients who hire them.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              { id: 'stat-invoiced', value: '$175M+', label: 'Invoiced' },
              { id: 'stat-invoices', value: '287K+', label: 'Invoices' },
              { id: 'stat-minutes', value: '21B+', label: 'Minutes tracked' },
            ].map((stat) => (
              <div key={stat.id} className="text-center p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <p className="text-white font-bold text-lg font-mono-nums">{stat.value}</p>
                <p className="text-2xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {[
              { id: 'feat-timer', text: 'Persistent timer across all pages' },
              { id: 'feat-invoice', text: 'Auto-generate invoices from timesheets' },
              { id: 'feat-reports', text: 'Visual reports and CSV export' },
              { id: 'feat-free', text: 'Free forever for freelancers' },
            ].map((f) => (
              <div key={f.id} className="flex items-center gap-2.5">
                <CheckCircle size={15} style={{ color: '#22C55E' }} className="flex-shrink-0" />
                <span className="text-body-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-2xs relative" style={{ color: 'rgba(255,255,255,0.35)' }}>
          © 2026 KlugTracker. Free for freelancers, always.
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo height={32} variant="dark" />
        </div>

        <div className="w-full max-w-md">
          {tab !== 'forgot' && (
            <div className="flex mb-8 border-b border-border">
              {(['login', 'signup'] as AuthTab[]).map((t) => (
                <button
                  key={`tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`pb-3 px-1 mr-6 text-sm font-semibold border-b-2 transition-colors ${
                    tab === t
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5 animate-fade-in">
              <div>
                <h2 className="text-title-lg mb-1">Welcome back</h2>
                <p className="text-body-sm text-muted-foreground">Sign in to your KlugTracker account</p>
              </div>

              <div>
                <label className="input-label">Email address</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="you@company.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                />
                {loginErrors.email && (
                  <p className="input-error-msg">{loginErrors.email}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="input-label" style={{ marginBottom: 0 }}>Password</label>
                  <button type="button" onClick={() => setTab('forgot')} className="text-body-sm text-primary hover:underline">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-8"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginErrors.password && (
                  <p className="input-error-msg">{loginErrors.password}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  className="w-4 h-4 rounded accent-primary"
                  checked={loginRemember}
                  onChange={(e) => setLoginRemember(e.target.checked)}
                />
                <label htmlFor="remember" className="text-body-sm text-foreground cursor-pointer">
                  Remember me for 30 days
                </label>
              </div>

              <button type="submit" className="btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Signing in...</>
                ) : (
                  <><ArrowRight size={16} /> Sign In</>
                )}
              </button>

              <div className="mt-6">
                <p className="text-xs text-muted-foreground text-center mb-3">— Try a demo account —</p>
                <div className="flex flex-col gap-2">
                  {[
                    { email: 'client@demo.com', name: 'Arjun Mehta', role: 'Client' },
                    { email: 'priya@demo.com', name: 'Priya Sharma', role: 'Freelancer' },
                    { email: 'rohan@demo.com', name: 'Rohan Patel', role: 'Freelancer' },
                  ].map((u) => (
                    <button
                      key={u.email}
                      type="button"
                      disabled={isLoading}
                      onClick={async () => {
                        setLoginEmail(u.email);
                        setLoginPassword('Demo@1234');
                        setLoginErrors({});
                        setIsLoading(true);
                        try {
                          const data = await login(u.email, 'Demo@1234');
                          localStorage.setItem('tt-role', data.user.activeRole);
                          toast.success(`Welcome, ${u.name}!`);
                          router.push('/dashboard');
                        } catch (err: any) {
                          toast.error(err?.message || 'Login failed');
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 text-left"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                          style={{ backgroundColor: '#000000' }}>
                          {u.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${
                        u.role === 'Client'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      }`}>{u.role}</span>
                    </button>
                  ))}
                </div>
              </div>

            </form>
          )}

          {/* SIGNUP FORM */}
          {tab === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-5 animate-fade-in">
              <div>
                <h2 className="text-title-lg mb-1">Create your account</h2>
                <p className="text-body-sm text-muted-foreground">Free forever. No credit card required.</p>
              </div>

              <div>
                <label className="input-label">I am a...</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['freelancer', 'client'] as const).map((r) => (
                    <button
                      key={`role-${r}`}
                      type="button"
                      onClick={() => setSelectedRole(r)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedRole === r
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Clock size={16} className={selectedRole === r ? 'text-primary' : 'text-muted-foreground'} />
                        <span className="text-sm font-semibold capitalize">{r}</span>
                      </div>
                      <p className="text-2xs text-muted-foreground">
                        {r === 'freelancer' ? 'Track time, submit timesheets' : 'Manage projects, approve work'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="input-label">Full name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Your full name"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                />
                {signupErrors.name && (
                  <p className="input-error-msg">{signupErrors.name}</p>
                )}
              </div>

              <div>
                <label className="input-label">Work email</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="you@company.com"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                />
                {signupErrors.email && (
                  <p className="input-error-msg">{signupErrors.email}</p>
                )}
              </div>

              <div>
                <label className="input-label">Password</label>
                <p className="text-body-sm text-muted-foreground mb-1">Minimum 8 characters</p>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-8"
                    placeholder="Create a strong password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupErrors.password && (
                  <p className="input-error-msg">{signupErrors.password}</p>
                )}
              </div>

              <div>
                <label className="input-label">Confirm password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-8"
                    placeholder="Re-enter your password"
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupErrors.confirmPassword && (
                  <p className="input-error-msg">{signupErrors.confirmPassword}</p>
                )}
              </div>

              <button type="submit" className="btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Creating account...</>
                ) : (
                  'Create Free Account'
                )}
              </button>

              <p className="text-body-sm text-muted-foreground text-center">
                By signing up, you agree to our{' '}
                <a href={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/terms-of-service`} className="text-primary hover:underline">Terms of Service</a>{' '}
                and{' '}
                <a href={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/privacy-policy`} className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {tab === 'forgot' && (
            <div className="animate-fade-in">
              {!forgotSent ? (
                <form onSubmit={handleForgot} className="space-y-5">
                  <div>
                    <button
                      type="button"
                      onClick={() => setTab('login')}
                      className="text-body-sm text-primary hover:underline mb-4 flex items-center gap-1"
                    >
                      ← Back to sign in
                    </button>
                    <h2 className="text-title-lg mb-1">Reset your password</h2>
                    <p className="text-body-sm text-muted-foreground">
                      Enter your email and we'll send a reset link valid for 1 hour.
                    </p>
                  </div>

                  <div>
                    <label className="input-label">Email address</label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder="you@company.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                    />
                    {forgotErrors.email && (
                      <p className="input-error-msg">{forgotErrors.email}</p>
                    )}
                  </div>

                  <button type="submit" className="btn-primary w-full" disabled={isLoading}>
                    {isLoading ? (
                      <><Loader2 size={16} className="animate-spin" /> Sending...</>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center space-y-4 animate-scale-in">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                    style={{ backgroundColor: 'var(--color-success-bg)' }}>
                    <CheckCircle size={28} style={{ color: 'var(--color-success)' }} />
                  </div>
                  <h2 className="text-title-lg">Check your inbox</h2>
                  <p className="text-body-sm text-muted-foreground">
                    We've sent a password reset link to your email. The link expires in 1 hour.
                  </p>
                  <button onClick={() => { setTab('login'); setForgotSent(false); }} className="btn-outlined btn-sm">
                    Back to sign in
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}